import { z } from 'zod';

import { customerSubscriptionStatusSchema } from '@/features/customers';
import { pageMetaSchema } from '@/lib/api/pagination';

/** What one row *is*: one physical **unit** a Customer owns — a `customer_devices` record — never
 *  a Customer, an Order or a catalog SKU. A Customer owning four units is four rows (CONTEXT.md
 *  "Row grain"; the design shows Lorri Warf four times). Units superseded by an approved
 *  replacement are excluded server-side via `activeCustomerDeviceSql` (ADO 102104). */
export const productsReportProductTypeSchema = z.enum(['device', 'component']);

/** One Products report row.
 *
 *  `subscriptionStatus` is **nullable**: `null` means the product is non-subscribable (every
 *  Component, and a non-subscribable Device), which the table renders as a hyphen rather than a
 *  chip — "no subscription" must look different from "subscription lapsed" (AC 112361).
 *
 *  The enum itself is `customerSubscriptionStatusSchema` — the one already shipped on Customer
 *  Detail, where ADO 112576 renamed `inactive` to `expired`. **Deliberately a single reference**:
 *  the Active/Expired labels are provisional pending BA on 112361 (comment 20841648), and reusing
 *  the shipped enum makes a reversal a one-place swap instead of two surfaces disagreeing.
 *
 *  `warrantyValidTill` + `isWarrantyExpired` are three states, not two — see `format.ts`. */
export const productsReportRowSchema = z.object({
  id: z.string(),
  customerId: z.string(),
  customerName: z.string(),
  phone: z.string(),
  productType: productsReportProductTypeSchema,
  // Nullable: a unit whose catalog Device/Component has since been deleted comes back with no
  // name. A strict string here rejected the whole page, so any sort that pulled such a row onto
  // page 1 rendered "No data found" over a populated response (QA 2026-09-28).
  productName: z.string().nullable(),
  subscriptionStatus: customerSubscriptionStatusSchema.nullable(),
  warrantyValidTill: z.string().nullable(),
  isWarrantyExpired: z.boolean(),
});

export const productsReportListResponseSchema = z.object({
  data: z.array(productsReportRowSchema),
  meta: pageMetaSchema,
});

/** The three filter fields, as the RHF draft holds them: always arrays, `[]` meaning "unrestricted".
 *
 *  `subscriptionStatus` reuses `customerSubscriptionStatusSchema` — **the same single reference**
 *  the row schema above already uses. That is deliberate and load-bearing: the Active/Expired
 *  labels are provisional pending BA on 112361 (comment 20841648), the deployed backend types say
 *  `active | inactive`, and one reference is what keeps a reversal from leaving the chip and the
 *  filter spelling the same concept two ways. Do not add a Reports-local status enum.
 *
 *  No `superRefine`: this panel has no From/To pair, so `enforceRangePairs` has nothing to guard. */
export const productsReportFiltersSchema = z.object({
  customerIds: z.array(z.string()),
  productType: z.array(productsReportProductTypeSchema),
  subscriptionStatus: z.array(customerSubscriptionStatusSchema),
});
